import React, { useCallback, useMemo, memo } from 'react';
import map from 'lodash/map';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import agentExperienceConfigResource from 'src/actions/agentExperienceConfig/agentExperienceConfigResource';
import { AgentUser } from './types/AgentUser';
import { EmptyStateWrapper } from 'src/presentation/components/pages/shared/Table';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import { FooterContainer, SelectedInfoContainer } from '@ad-scripts/presentation/components/common/dropdown_menu_v2';
import fuzzySearch from 'src/presentation/components/pages/shared/utils/fuzzySearch';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import { MultiSelectDropdownMenu } from 'src/presentation/components/agent_desktop_components';
import { SectionSplitter } from '@ad-scripts/presentation/components/common/multi_select_dropdown_menu';
import { StringArrayColumn } from 'src/presentation/components/pages/shared/StringArrayColumn';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import useAgentUsers from 'src/presentation/components/pages/people/users/hooks/useAgentUsers';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import UserEditor from './UserEditor';
import UsersActions from './UsersActions';
import UsersSettings from './UsersSettings';
import usersResource from 'src/actions/users/usersResource';
import UsersRowActions from './UsersRowActions';
import UserStatus from './UserStatus';
import { UserStatuses } from 'src/models/Users/User';

const mapToQuery = (list: string[]) => `status=${list.filter(query => Boolean(query)).join('&status=')}`;
const parseFromQuery = query => query.getAll('status');

const userStatusOptions: UserOption[] = [
  { value: UserStatuses.ACTIVE, label: 'Active' },
  { value: UserStatuses.INACTIVE, label: 'Invite sent' },
  { value: UserStatuses.DISABLED, label: 'Deactivated' },
  { value: UserStatuses.INVITE_EXPIRED, label: 'Invite expired' },
];

const allUserStatues = map(userStatusOptions, status => status.value);

const Users: TablePageRender = ({ root }) => {
  const history = useHistory();
  const { search } = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const statusesFromQuery = useMemo(() => parseFromQuery(query), [query]);
  const statusOptions = query.has('status') ? statusesFromQuery : allUserStatues;

  const { agentUsers = [], errors, loadingState } = useAgentUsers();
  const agentExperienceConfigRes = useDocumentResource(agentExperienceConfigResource);
  const isFeatureEnabled = useIsFeatureEnabled();
  const isUserManagementEnabled = isFeatureEnabled('userManagement');
  const usersByStatuses: AgentUser[] = agentUsers.filter(item => statusOptions.some(option => item.status === option));

  const handleSelectStatus = useCallback(
    selectedOptions => {
      history.push({ search: mapToQuery(selectedOptions) });
    },
    [history]
  );

  const renderUsersRowActions = useCallback(
    ({ item }) => {
      return isUserManagementEnabled ? <UsersRowActions item={item} root={root} /> : null;
    },
    [root, isUserManagementEnabled]
  );

  const actions = isUserManagementEnabled ? <UsersActions root={root} /> : null;

  const emptyStateRender = (
    <EmptyStateWrapper>
      <H3>No users found</H3>
      <p>Adjust your filters and try again</p>
    </EmptyStateWrapper>
  );

  const statusFilter = (
    <StyledMultiSelectDropdownMenu
      getDropdownLabel={getSelectedStatusesText}
      onSelect={handleSelectStatus}
      options={userStatusOptions}
      searchable={false}
      selectedOptionsFirst={false}
      values={statusOptions}
    />
  );

  return (
    <Switch>
      {isUserManagementEnabled ? (
        <Route
          exact
          path={`/users/settings`}
          key="settings"
          render={() => {
            return <UsersSettings resource={agentExperienceConfigRes} />;
          }}
        />
      ) : null}
      <Route
        path="/users"
        key="fallback"
        render={() => {
          return (
            <TablePage
              actions={actions}
              columnConfig={columnConfig}
              data-aid={dataAidPrefix}
              description={description(isUserManagementEnabled)}
              editor={UserEditor}
              errors={errors}
              filterFunc={getFilterFuncHandler()}
              initialSorting={sortingConfig}
              isLoading={isLoadingStatus(loadingState)}
              items={usersByStatuses}
              name="Users"
              pagination={paginationConfig}
              renderRowActions={renderUsersRowActions}
              resetErrors={usersResource.actions.resetErrors}
              root={root}
              emptyStateRender={emptyStateRender}
              leftBottomHeaderSlot={statusFilter}
            />
          );
        }}
      />
    </Switch>
  );
};

const columnConfig = [
  {
    attr: 'displayName',
    label: 'Name',
    sorting: true,
    customColumnStyle: () => ({ width: '240px' }),
  },
  {
    attr: 'email',
    label: 'Email',
    sorting: true,
    customColumnStyle: () => ({ width: '275px' }),
  },
  {
    attr: 'agentRoles',
    label: 'Roles',
    sorting: true,
    customColumnStyle: () => ({ width: '270px' }),
    customRenderer: item => <StringArrayColumn attr="agentRoles" item={item} />,
  },
  {
    attr: 'teams',
    label: 'Teams',
    sorting: true,
    customColumnStyle: () => ({ width: '80px', textAlign: 'right' }),
    customStyle: () => ({ textAlign: 'right' }),
    defaultValue: '0',
  },
  {
    attr: 'inboxes',
    label: 'Inboxes',
    sorting: true,
    customColumnStyle: () => ({ width: '80px', textAlign: 'right' }),
    customStyle: () => ({ textAlign: 'right' }),
    defaultValue: 0,
  },
  {
    attr: 'status',
    label: 'Status',
    sorting: true,
    customColumnStyle: () => ({ width: '175px' }),
    customRenderer: user => <UserStatus user={user} />,
  },
];

const getFilterFuncHandler = () => {
  const keys = ['displayName', 'username', 'agentRoles'];
  return fuzzySearch({ keys });
};

const description = isEnableUserManagement =>
  isEnableUserManagement ? (
    <div>
      Manage account roles and permissions for people at your company.{' '}
      <ExternalLink
        label="Learn more about user management"
        to="https://help.gladly.com/docs/managing-users-and-roles"
      />
    </div>
  ) : (
    <div>
      View account roles and permissions for people at your company.{' '}
      <ExternalLink
        data-aid="learn-about-roles-and-permissions"
        label="Learn about roles and permissions"
        to="https://help.gladly.com/docs/managing-users-and-roles#roles-and-responsibilities"
      />
    </div>
  );
const sortingConfig = { attr: 'displayName' };
const paginationConfig = { pageSize: 50, itemsTitle: 'users' };
const dataAidPrefix = 'admin-users-page';

const StyledMultiSelectDropdownMenu = styled(MultiSelectDropdownMenu)`
  width: 208px;
  margin-top: 14px;

  ${SelectedInfoContainer}, ${FooterContainer} {
    display: none;
  }

  ${SectionSplitter} {
    border-bottom: 0;
  }
`;

interface UserOption {
  value: UserStatuses;
  label: string;
}

const getSelectedStatusesText = (selected, placeholder) => {
  switch (selected.length) {
    case 0:
      return placeholder || 'None';
    case 1:
      return `${selected[0].label}`;
    case 2:
      return `${selected[0].label} and ${selected[1].label}`;
    case 3:
      return `${selected[0].label}, ${selected[1].label} and ${selected[2].label}`;
    case 4:
      return `All users`;
    default:
      return `${selected[0].label} and +${selected.length - 1} more`;
  }
};

export default memo(Users);
